import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, IconButton } from '@ui/button';
import { TooltipIcon } from '@web/components';
import { Input } from '@web/atoms';
import { ResponsiveImage } from '@web/molecules/ResponsiveImage';
import { StyledFormWithImage } from '../StyledForm';
import { useShopifyCartContext } from '../OnboardingFlowContainer/ShopifyCartProvider';
import { Question } from '../Question';
import { FormFieldsContainer, FormWrapper } from '../FormFieldsContainer';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { onboardingSubmitted, onboardingBackArrowClicked } from '../events';
import { schema } from './schema';
import {
  FieldWrapper,
  ImageContainer,
  ArrowContainer,
  MobileImageContainer,
  DesktopImageContainer,
  ButtonContainer,
} from './styles.js';

export const CaloriesForm = ({
  defaultValues,
  goBack,
  onSubmit,
  index,
  weightLabel,
  weightQuestionPrompt,
  weightQuestionSubheadline,
  weightCallout,
  idealWeightLabel,
  puppyIdealWeightLabel,
  isPuppy,
  idealWeightHelp,
  ctaButtonText,
  mobileImage,
  desktopImage,
  flowType,
}) => {
  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isSubmitted, isSubmitSuccessful },
    reset,
    getValues,
  } = useForm({
    defaultValues,
    mode: 'onBlur',
    resolver: yupResolver(schema),
  });

  const reporter = useReporter();
  const { customer } = useSessionCustomer();
  const { cart } = useShopifyCartContext();

  const goBackWithReporter = () => {
    reporter.tag(
      onboardingBackArrowClicked('weight_step', cart?.customer || customer),
    );
    goBack();
  };

  const submitForm = async data => {
    reporter.tag(
      onboardingSubmitted(
        index,
        'dog_weight',
        cart?.customer || customer,
        data,
        flowType,
      ),
    );
    const result = await onSubmit(data);
    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
        }),
      ),
    );
  };

  const idealWeightCopy = isPuppy ? puppyIdealWeightLabel : idealWeightLabel;

  const idealWeightTooltip = (
    <TooltipIcon
      name="Display weight information"
      variant="info"
      placement="top"
      height={false}
    >
      {idealWeightHelp}
    </TooltipIcon>
  );

  const isInSubmittingState =
    isSubmitting || (isSubmitted && isSubmitSuccessful);

  const [isInputFocused, setIsInputFocused] = useState(false);
  const [hasFocused, setHasFocused] = useState(false);

  return (
    <StyledFormWithImage onSubmit={handleSubmit(submitForm)}>
      <ImageContainer>
        <ArrowContainer aria-live="polite">
          <IconButton
            aria-label="go to previous step"
            icon={{
              iconName: 'arrow-left',
              size: '24px',
              alt: 'back',
              id: 'prev_step_footer',
              color: 'background.lightest',
            }}
            onClick={goBackWithReporter}
            disabled={isSubmitting}
            type="button"
            variant="primary"
          />
        </ArrowContainer>
        <MobileImageContainer>
          <ResponsiveImage priority={true} {...mobileImage} width={400} />
        </MobileImageContainer>
        <DesktopImageContainer>
          <ResponsiveImage
            priority={true}
            {...desktopImage}
            height={362}
            width={406}
          />
        </DesktopImageContainer>
      </ImageContainer>
      <FormWrapper
        $minHeight={
          isInputFocused && weightCallout && !hasFocused ? '450' : '253'
        }
      >
        <FormFieldsContainer $maxWidth={391}>
          <Question
            subheadline={weightQuestionSubheadline}
            prompt={weightQuestionPrompt}
            callout={weightCallout}
            calloutMargin={[4.5, 0, 1]}
            hideCaptionOrCallout={isInputFocused || hasFocused}
            gap={1.5}
            typography={'heading4'}
          >
            <FieldWrapper>
              <Controller
                control={control}
                name="weight"
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <Input
                    {...field}
                    type="number"
                    labelText={weightLabel}
                    value={field.value || ''}
                    status={error?.message ? 'error' : null}
                    error={error?.message || ''}
                    visuallyHideLabel
                    onFocus={() => setIsInputFocused(true)}
                    onBlur={() => {
                      setTimeout(() => {
                        setIsInputFocused(false);
                        setHasFocused(true);
                      }, 200);
                    }}
                  />
                )}
              />
            </FieldWrapper>
            <FieldWrapper>
              <Controller
                control={control}
                name="idealWeight"
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <Input
                    {...field}
                    type="number"
                    labelText={idealWeightCopy}
                    icon={idealWeightTooltip}
                    value={field.value || ''}
                    status={error?.message ? 'error' : null}
                    error={error?.message || ''}
                    visuallyHideLabel
                    onFocus={() => setIsInputFocused(true)}
                  />
                )}
              />
            </FieldWrapper>
          </Question>
          <ButtonContainer>
            <Button
              type="submit"
              aria-label={ctaButtonText}
              disabled={isInSubmittingState}
              variant="primary"
              isLoading={isInSubmittingState}
              horizontalPadding={32}
            >
              {ctaButtonText}
            </Button>
          </ButtonContainer>
        </FormFieldsContainer>
      </FormWrapper>
    </StyledFormWithImage>
  );
};

CaloriesForm.displayName = 'CaloriesForm';

CaloriesForm.propTypes = {
  defaultValues: PropTypes.shape({
    weight: PropTypes.number,
    idealWeight: PropTypes.number,
  }).isRequired,
  weightLabel: PropTypes.string.isRequired,
  weightQuestionPrompt: PropTypes.string.isRequired,
  weightQuestionSubheadline: PropTypes.string,
  weightCallout: PropTypes.object,
  idealWeightLabel: PropTypes.string.isRequired,
  isPuppy: PropTypes.bool.isRequired,
  goBack: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  puppyIdealWeightHelp: PropTypes.string.isRequired,
  idealWeightHelp: PropTypes.string.isRequired,
  puppyIdealWeightLabel: PropTypes.string.isRequired,
  ctaButtonText: PropTypes.string,
  mobileImage: PropTypes.object.isRequired,
  desktopImage: PropTypes.object.isRequired,
  flowType: PropTypes.string,
};
